import { CAP } from "../data/mockData";
import { CheckIcon, CloseIcon, MailIcon, UploadIcon } from "../components/icons";
import { resolveImage } from "../data/assetMap";
import Spinner from "../components/Spinner";

const thumbStyle = (d) => ({
  backgroundColor: d.kind === "PDF" ? "#FDECEC" : "#E7F1FB",
  backgroundImage: `url("${resolveImage(d.prevUrl || d.prev)}")`,
});

export default function DocumentsStep({
  docs,
  docsFor,
  categories,
  autoOpen,
  onToggleAutoOpen,
  openCat,
  onToggleCat,
  over,
  drag,
  onDragStartDoc,
  onDragEndDoc,
  onDragOverReq,
  onDragLeaveReq,
  onDropReq,
  onTagDoc,
  onPreviewDoc,
  onUploadReq,
  onUntagDoc,
  onRemoveDoc,
  fileRef,
  onFiles,
  bankReqId,
  govReqId,
  capFor = () => CAP,
  categoriesLoading,
  showReqErrors,
  // View-only lead: files can be previewed, never uploaded/tagged/removed.
  readOnly,
}) {
  const untaggedDocs = docs.filter((d) => !d.req);
  const govDocs = docs.filter((d) => d.req === govReqId && d.govType);
  const govUsed = [];
  govDocs.forEach((d) => {
    if (!govUsed.includes(d.govType)) govUsed.push(d.govType);
  });
  const bankTagged = docs.filter((d) => d.req === bankReqId && d.bankName).map((d) => d.bankName);

  const totalReqs = categories.reduce((n, c) => n + c.reqs.length, 0);
  const totalMet = categories.reduce((n, c) => n + c.reqs.filter((r, i) => docsFor(c.key + "-" + i).length > 0).length, 0);

  return (
    <div>
      {untaggedDocs.length > 0 && (
        <div className="fetch-card">
          <span className="fetch-card__icon">
            <MailIcon size={17} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="fetch-card__title">
              {untaggedDocs.length} document{untaggedDocs.length === 1 ? "" : "s"} auto-fetched from emails
            </span>
            <span className="fetch-card__sub">
              Tag each doc to the right category to build the checklist. {totalMet} of {totalReqs} requirements met.
            </span>
          </span>
        </div>
      )}

      <div className="accordion">
        <button type="button" className="accordion__toggle" onClick={onToggleAutoOpen}>
          <span className="accordion__titles">
            <span className="accordion__title">Auto-Fetched Docs</span>
            <span className="accordion__sub">From email · awaiting categorization</span>
          </span>
          <span className="accordion__meta">
            <span className={"accordion__badge" + (untaggedDocs.length === 0 ? " accordion__badge--done" : "")}>
              {untaggedDocs.length ? untaggedDocs.length + " new" : "Cleared"}
            </span>
            <span className="accordion__sign">{autoOpen ? "−" : "+"}</span>
          </span>
        </button>
        {autoOpen && (
          <div style={{ padding: "14px 0 18px", borderBottom: "1px solid var(--ink-100)" }}>
            {untaggedDocs.length === 0 ? (
              <div className="muted-copy">All auto-fetched documents have been tagged. New emails and untagged docs will appear here.</div>
            ) : (
              <div className="untagged-grid">
                {untaggedDocs.map((d) => (
                  <div
                    key={d.id}
                    draggable={!readOnly}
                    onDragStart={() => onDragStartDoc(d.id)}
                    onDragEnd={onDragEndDoc}
                    className={"untagged-card" + (drag === d.id ? " untagged-card--drag" : "")}
                  >
                    <span className="untagged-card__thumb" style={thumbStyle(d)} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="untagged-card__name">{d.name}</span>
                      <span className="untagged-card__meta">
                        {d.size} · {d.from} · {d.when}
                      </span>
                      <span className="untagged-card__actions">
                        {!readOnly && (
                          <button type="button" className="btn-tag" onClick={() => onTagDoc(d.id)}>
                            Tag
                          </button>
                        )}
                        <button type="button" className="btn-preview" onClick={() => onPreviewDoc(d.id)}>
                          Preview
                        </button>
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {categoriesLoading && categories.length === 0 && (
        <div className="muted-copy" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Spinner /> Loading document requirements…
        </div>
      )}

      {categories.map((c) => {
        const met = c.reqs.filter((r, i) => docsFor(c.key + "-" + i).length > 0).length;
        const allMet = met === c.reqs.length;
        const open = openCat === c.key;
        return (
          <div className="accordion" key={c.key}>
            <button type="button" className="accordion__toggle" onClick={() => onToggleCat(c.key)}>
              <span className="accordion__titles">
                <span className="accordion__title">{c.name}</span>
                {c.sub && <span className="accordion__sub">{c.sub}</span>}
              </span>
              <span className="accordion__meta">
                <span className={"accordion__badge" + (allMet ? " accordion__badge--done" : "")}>
                  {met}/{c.reqs.length}
                </span>
                <span className="accordion__sign">{open ? "−" : "+"}</span>
              </span>
            </button>
            {open && (
              <div className="req-list">
                {c.reqs.map((label, i) => {
                  const reqId = c.key + "-" + i;
                  const files = docsFor(reqId);
                  const cap = capFor(reqId);
                  const full = files.length >= cap;
                  const hot = over === reqId;
                  const required = !!c.required?.[i];
                  const min = c.minUpload?.[i] || 1;
                  const missing = showReqErrors && !!(c.required && c.required[i]) && files.length === 0;

                  const tags = [];
                  if (reqId === govReqId) {
                    if (govUsed[0]) tags.push({ label: "Primary: " + govUsed[0], style: { background: "#E3F0FF", color: "#2F6BC4" } });
                    if (govUsed[1]) tags.push({ label: "Secondary: " + govUsed[1], style: { background: "#F0E5FF", color: "#7B47C9" } });
                    if (govDocs.length >= 2 && govUsed.length < 2)
                      tags.push({ label: "Need 2 different types", style: { background: "#FFF3D6", color: "#9A6700" } });
                  }
                  if (reqId === bankReqId) {
                    bankTagged.forEach((b) => tags.push({ label: b, style: { background: "#E8F5E9", color: "#2E7D32" } }));
                  }

                  return (
                    <div
                      key={reqId}
                      className={"req-row" + (hot ? " req-row--hot" : "") + (missing ? " req-row--error" : "")}
                      onDragOver={(e) => {
                        if (readOnly) return;
                        e.preventDefault();
                        onDragOverReq(reqId);
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget)) return;
                        onDragLeaveReq(reqId);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (readOnly) return;
                        onDropReq(reqId, e);
                      }}
                    >
                      <div className="req-row__head">
                        <span className={"req-row__tick" + (files.length ? " req-row__tick--on" : "")}>
                          {files.length > 0 && <CheckIcon size={11} stroke="#fff" width={3} />}
                        </span>
                        <span className="req-row__label" style={{ color: files.length ? "#1F1F1F" : "#444444" }}>
                          {label}
                        </span>
                        <span
                          className="req-row__count"
                          style={{
                            background: files.length ? "var(--success-bg)" : missing ? "var(--danger-bg)" : "var(--ink-050)",
                            color: files.length ? "var(--success)" : missing ? "var(--danger)" : "var(--ink-500)",
                          }}
                        >
                          {files.length ? files.length + "/" + cap : required ? "Required" : "Optional"}
                        </span>
                      </div>
                      {missing && (
                        <div className="req-row__error">
                          {min > 1 ? "Upload at least " + min + " files" : "This document is required"}
                        </div>
                      )}

                      {tags.length > 0 && (
                        <div className="req-row__tags">
                          {tags.map((t) => (
                            <span key={t.label} className="req-row__tag" style={t.style}>
                              {t.label}
                            </span>
                          ))}
                        </div>
                      )}

                      {files.map((d) => (
                        <div key={d.id} className="req-doc-row">
                          <span className="req-doc-row__thumb" style={thumbStyle(d)} />
                          <span style={{ flex: "1 1 150px", minWidth: 0 }}>
                            <span className="req-doc-row__name">{d.name}</span>
                            <span className="req-doc-row__meta">
                              {(d.govType || d.bankName ? (d.govType || d.bankName) + " · " : "") + d.size}
                            </span>
                          </span>
                          <span className="req-doc-row__actions">
                            <button type="button" className="btn-preview" onClick={() => onPreviewDoc(d.id)}>
                              Preview
                            </button>
                            {!readOnly && /^a/.test(d.id) && (
                              <button type="button" className="btn-preview" onClick={() => onUntagDoc(d.id, d.name)}>
                                Untag
                              </button>
                            )}
                            {!readOnly && (
                              <button type="button" className="btn-remove" aria-label="Remove" onClick={() => onRemoveDoc(d.id, d.name)}>
                                <CloseIcon size={13} />
                              </button>
                            )}
                          </span>
                        </div>
                      ))}

                      {readOnly && files.length === 0 && <div className="req-row__full">No document uploaded</div>}
                      {!readOnly && !full && (
                        <button type="button" className={"req-row__drop" + (hot ? " req-row__drop--hot" : "")} onClick={() => onUploadReq(reqId)}>
                          <UploadIcon size={15} />
                          <span>{hot ? (drag ? "Drop to tag here" : "Drop to upload here") : files.length ? "Add another file" : "Upload or drop a file here"}</span>
                        </button>
                      )}
                      {!readOnly && full && <div className="req-row__full">
                          {reqId === bankReqId ? "One form attached per selected financier (" + cap + "/" + cap + ")" : "Maximum " + cap + " files reached"}
                        </div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <input type="file" multiple ref={fileRef} onChange={onFiles} style={{ display: "none" }} />
    </div>
  );
}
